<script setup>
import { computed } from 'vue'

const props = defineProps({
  sectionKey: { type: String, required: true },
  fieldKey:   { type: String, required: true },
  field:      { type: Object, required: true },
  content:    { type: Object, required: true },
})

const emit = defineEmits(['image-pick'])

const liveField = computed(() =>
  props.content[props.sectionKey]?.[props.fieldKey]
)

const currentValue = computed(() =>
  liveField.value?.value ?? props.field.value
)

// `content` is the shared reactive store object from websiteBuilderStore.js, passed
// down by reference so edits here update the store directly (no event plumbing) —
// intentional, not the usual copied-prop case vue/no-mutating-props guards against.
function ensureField() {
  /* eslint-disable vue/no-mutating-props */
  if (!props.content[props.sectionKey]) {
    props.content[props.sectionKey] = {}
  }
  if (!props.content[props.sectionKey][props.fieldKey]) {
    const v = props.field.value
    props.content[props.sectionKey][props.fieldKey] = {
      ...props.field,
      value: Array.isArray(v) ? [...v] : v,
    }
  }
  /* eslint-enable vue/no-mutating-props */
  return props.content[props.sectionKey][props.fieldKey]
}

function setValue(val) {
  ensureField().value = val
}

function updateArrayItem(index, val) {
  ensureField().value[index] = val
}

function addArrayItem() {
  ensureField().value.push('')
}

function removeArrayItem(index) {
  ensureField().value.splice(index, 1)
}

function updateObjectItem(itemIndex, subKey, val) {
  ensureField().value[itemIndex][subKey] = val
}

function addObjectItem() {
  const arr = ensureField().value
  const first = arr[0]
  arr.push(first
    ? Object.fromEntries(Object.keys(first).map(k => [k, '']))
    : {}
  )
}

function removeObjectItem(index) {
  ensureField().value.splice(index, 1)
}

const TEXTAREA_KEYS = new Set(['description', 'body', 'quote', 'summary', 'tagline', 'sub'])

function inferSubType(key) {
  if (key === 'url' || key.includes('image')) return 'image'
  if (key.includes('video')) return 'video'
  if (key.includes('email')) return 'email'
  if (TEXTAREA_KEYS.has(key)) return 'textarea'
  return 'text'
}
</script>

<template>
  <!-- text / email / video -->
  <v-text-field
    v-if="['text', 'email', 'video'].includes(field.type)"
    :label="field.label"
    :model-value="currentValue"
    variant="outlined"
    hide-details="auto"
    @update:model-value="setValue($event)"
  />

  <!-- textarea -->
  <v-textarea
    v-else-if="field.type === 'textarea'"
    :label="field.label"
    :model-value="currentValue"
    variant="outlined"
    hide-details="auto"
    rows="3"
    auto-grow
    @update:model-value="setValue($event)"
  />

  <!-- image -->
  <div v-else-if="field.type === 'image'" class="image-field">
    <div class="image-field-label">{{ field.label }}</div>
    <button
      type="button"
      class="image-field-preview"
      @click="emit('image-pick', { sectionKey, fieldKey })"
    >
      <img v-if="currentValue" :src="currentValue" alt="" />
      <span v-else class="image-field-placeholder">
        <v-icon size="28">mdi-image-plus-outline</v-icon>
        <span>Choose image</span>
      </span>
    </button>
    <v-text-field
      label="Image URL"
      :model-value="currentValue"
      variant="outlined"
      hide-details="auto"
      density="compact"
      class="mt-2"
      @update:model-value="setValue($event)"
    />
  </div>

  <!-- array:text -->
  <div v-else-if="field.type === 'array:text'">
    <div class="array-field-header">
      <span>{{ field.label }}</span>
      <v-btn icon="mdi-plus" size="x-small" variant="text" @click="addArrayItem()" />
    </div>
    <div
      v-for="(item, index) in currentValue"
      :key="index"
      class="array-field-row"
    >
      <v-text-field
        :label="`${field.label} ${index + 1}`"
        :model-value="item"
        variant="outlined"
        hide-details="auto"
        @update:model-value="updateArrayItem(index, $event)"
      />
      <v-btn icon="mdi-close" size="x-small" variant="text" @click="removeArrayItem(index)" />
    </div>
  </div>

  <!-- array:object -->
  <div v-else-if="field.type === 'array:object'">
    <div class="array-field-header">
      <span>{{ field.label }}</span>
      <v-btn icon="mdi-plus" size="x-small" variant="text" @click="addObjectItem()" />
    </div>
    <div
      v-for="(item, itemIndex) in currentValue"
      :key="itemIndex"
      class="array-object-card"
    >
      <div class="array-object-card-header">
        <span>{{ field.label }} {{ itemIndex + 1 }}</span>
        <v-btn icon="mdi-close" size="x-small" variant="text" @click="removeObjectItem(itemIndex)" />
      </div>
      <template v-for="(val, subKey) in item" :key="subKey">
        <v-textarea
          v-if="inferSubType(String(subKey)) === 'textarea'"
          :label="String(subKey)"
          :model-value="val"
          variant="outlined"
          hide-details="auto"
          rows="2"
          auto-grow
          class="mb-2"
          @update:model-value="updateObjectItem(itemIndex, subKey, $event)"
        />
        <v-text-field
          v-else
          :label="String(subKey)"
          :model-value="val"
          variant="outlined"
          hide-details="auto"
          class="mb-2"
          @update:model-value="updateObjectItem(itemIndex, subKey, $event)"
        />
      </template>
    </div>
  </div>
</template>

<style scoped>
.image-field {
  display: grid;
  gap: 8px;
}

.image-field-label {
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-primary);
}

.image-field-preview {
  width: 100%;
  aspect-ratio: 16 / 9;
  display: grid;
  place-items: center;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-alt);
  cursor: pointer;
}

.image-field-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.image-field-placeholder {
  display: grid;
  place-items: center;
  gap: 6px;
  color: var(--text-secondary);
  font-size: 0.8rem;
}

.array-field-header {
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-weight: 850;
}

.array-field-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px;
  align-items: start;
  gap: 6px;
  margin-bottom: 8px;
}

.array-object-card {
  padding: 10px;
  margin-bottom: 10px;
  border: 1px solid var(--border-subtle);
  border-radius: 6px;
  background: var(--surface-alt);
  display: grid;
  gap: 6px;
}

.array-object-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
  color: var(--text-primary);
  font-size: 0.8rem;
  font-weight: 850;
}
</style>
